<template>
  <section class="facilities-page" :dir="isArabic ? 'rtl' : 'ltr'">
    <!-- Header -->
    <div class="header">
      <h1>{{ t("facilities.title") }}</h1>

      <p>
        {{ t("facilities.description") }}
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-wrapper">
      <div class="loading-card">
        <v-progress-circular
          indeterminate
          size="70"
          width="6"
          color="primary"
        />

        <h3>{{ t("facilities.loadingTitle") }}</h3>

        <p>{{ t("facilities.loadingDescription") }}</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="errorMessage" class="error-box">
      {{ errorMessage }}
    </div>

    <template v-else>
      <!-- Filters -->
      <div class="toolbar">
        <!-- Governorate -->
        <select v-model="governorateFilter" @change="districtFilter = ''">
          <option value="">
            {{ t("facilities.filters.allGovernorates") }}
          </option>

          <option
            v-for="governorate in governorates"
            :key="governorate"
            :value="governorate"
          >
            {{ governorate }}
          </option>
        </select>

        <!-- District -->
        <select v-model="districtFilter">
          <option value="">
            {{ t("facilities.filters.allDistricts") }}
          </option>

          <option
            v-for="district in districts"
            :key="district"
            :value="district"
          >
            {{ district }}
          </option>
        </select>

        <!-- Priority -->
        <select v-model="priorityFilter">
          <option value="">
            {{ t("facilities.filters.allPriorities") }}
          </option>

          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
          <option value="P4">P4</option>
        </select>

        <!-- Facility type -->
        <select v-model="typeFilter">
          <option value="">
            {{ t("facilities.filters.allTypes") }}
          </option>

          <option v-for="type in facilityTypes" :key="type" :value="type">
            {{ type }}
          </option>
        </select>

        <!-- Search -->
        <input
          v-model="search"
          type="text"
          :placeholder="t('facilities.filters.searchPlaceholder')"
        />

        <!-- Count -->
        <div class="count">
          {{
            t("facilities.count", {
              filtered: filteredFacilities.length,
              total: facilities.length,
            })
          }}
        </div>
      </div>

      <!-- Table -->
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>{{ t("facilities.table.facility") }}</th>
              <th>{{ t("facilities.table.type") }}</th>
              <th>{{ t("facilities.table.governorate") }}</th>
              <th>{{ t("facilities.table.district") }}</th>
              <th>{{ t("facilities.table.operationalStatus") }}</th>
              <th>{{ t("facilities.table.staff") }}</th>
              <th>{{ t("facilities.table.beneficiaries") }}</th>
              <th>{{ t("facilities.table.population") }}</th>
              <th>{{ t("facilities.table.fis") }}</th>
              <th>{{ t("facilities.table.priority") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="facility in filteredFacilities"
              :key="facility.id"
              @click="openFacility(facility)"
            >
              <td
                class="facility-name"
                :data-label="t('facilities.table.facility')"
              >
                {{ facility.name }}
              </td>

              <td :data-label="t('facilities.table.type')">
                {{ facility.type }}
              </td>

              <td :data-label="t('facilities.table.governorate')">
                {{ facility.governorate }}
              </td>

              <td :data-label="t('facilities.table.district')">
                {{ facility.district }}
              </td>

              <td :data-label="t('facilities.table.operationalStatus')">
                <span class="status-pill" :class="facility.statusClass">
                  {{ facility.operationalStatus }}
                </span>
              </td>

              <td :data-label="t('facilities.table.staff')">
                {{ facility.totalStaff }}
              </td>

              <td :data-label="t('facilities.table.beneficiaries')">
                {{ facility.beneficiariesMonth }}
              </td>

              <td :data-label="t('facilities.table.population')">
                {{ facility.population }}
              </td>

              <td class="fis" data-label="FIS">
                {{ facility.fis }}
              </td>
              <td :data-label="t('facilities.table.priority')">
                <span class="priority-pill" :class="facility.priority">
                  {{ facility.priority }}
                </span>
                {{
                  isArabic
                    ? (facility.fis_priority?.split("|")[0] ?? "").trim()
                    : (
                        facility.fis_priority?.split("|")[1] ??
                        facility.fis_priority ??
                        ""
                      ).trim()
                }}
              </td>
            </tr>

            <tr v-if="filteredFacilities.length === 0">
              <td colspan="10" class="empty-row">
                {{ t("facilities.empty") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";

const router = useRouter();

const { t, locale } = useI18n();

const isArabic = computed(() => locale.value === "ar");

const search = ref("");
const typeFilter = ref("");
const priorityFilter = ref("");
const governorateFilter = ref("");
const districtFilter = ref("");

const loading = ref(true);
const errorMessage = ref("");

const rawFacilities = ref([]);

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "-";
  }

  return number.toLocaleString("en-US");
};

const formatFis = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0.0";
  }

  return number.toFixed(1);
};

/*
|--------------------------------------------------------------------------
| Status class
|--------------------------------------------------------------------------
*/

const getStatusClass = (statusAr, statusEn) => {
  const arabicStatus = String(statusAr || "").toLowerCase();
  const englishStatus = String(statusEn || "").toLowerCase();

  if (
    arabicStatus.includes("لا تعمل") ||
    arabicStatus.includes("متوقفة") ||
    englishStatus.includes("not working") ||
    englishStatus.includes("closed") ||
    englishStatus.includes("non-operational")
  ) {
    return "status-danger";
  }

  if (
    arabicStatus.includes("جزئياً") ||
    arabicStatus.includes("جزئيا") ||
    englishStatus.includes("partial")
  ) {
    return "status-partial";
  }

  if (
    arabicStatus.includes("تعمل بالكامل") ||
    arabicStatus.includes("تعمل كلياً") ||
    arabicStatus.includes("تعمل كليا") ||
    englishStatus.includes("fully") ||
    englishStatus.includes("full operation")
  ) {
    return "status-full";
  }

  return "status-medium";
};

/*
|--------------------------------------------------------------------------
| Normalize facility
|--------------------------------------------------------------------------
*/

const normalizeFacility = (item, index) => {
  const facilityName = isArabic.value
    ? item.facility_name_ar ||
      item.facility_name_en ||
      item.facility_name ||
      "-"
    : item.facility_name_en ||
      item.facility_name_ar ||
      item.facility_name ||
      "-";

  const facilityType = isArabic.value
    ? item.facility_type_ar ||
      item.facility_type_en ||
      item.facility_type ||
      "-"
    : item.facility_type_en ||
      item.facility_type_ar ||
      item.facility_type ||
      "-";

  const governorate = isArabic.value
    ? item.governorate_name_ar ||
      item.governorate_name_en ||
      item.governorate ||
      "-"
    : item.governorate_name_en ||
      item.governorate_name_ar ||
      item.governorate ||
      "-";

  const district = isArabic.value
    ? item.district_name_ar || item.district_name_en || item.district || "-"
    : item.district_name_en || item.district_name_ar || item.district || "-";

  const operationalStatus = isArabic.value
    ? item.operational_status_ar ||
      item.operational_status_en ||
      item.operational_status ||
      "-"
    : item.operational_status_en ||
      item.operational_status_ar ||
      item.operational_status ||
      "-";

  return {
    id: item.survey_id || item.id || index + 1,

    surveyId: item.survey_id || item.id || index + 1,

    name: facilityName,

    type: facilityType,

    governorate,

    district,

    operationalStatus,

    statusClass: getStatusClass(
      item.operational_status_ar,
      item.operational_status_en
    ),

    totalStaff: formatNumber(item.cadre_total_count ?? item.total_staff),

    beneficiariesMonth: formatNumber(
      item.beneficiaries_current ?? item.beneficiaries_month
    ),

    population: formatNumber(item.target_population),

    fis: formatFis(item.FIS_result ?? item.FIS ?? item.fis),

    fis_priority: item.FIS_priority || "-",
    priority: item.priority_code || item.priority || item.periority || "P4",
  };
};

/*
|--------------------------------------------------------------------------
| Facilities based on current language
|--------------------------------------------------------------------------
*/

const facilities = computed(() => {
  return rawFacilities.value.map((item, index) =>
    normalizeFacility(item, index)
  );
});

/*
|--------------------------------------------------------------------------
| Load facilities
|--------------------------------------------------------------------------
*/

const loadFacilities = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await api.get("/health-facilities");

    const data = response.data?.data || response.data || [];

    rawFacilities.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Failed to load health facilities:", error);

    errorMessage.value = t("facilities.loadError");
  } finally {
    loading.value = false;
  }
};

/*
|--------------------------------------------------------------------------
| Governorates
|--------------------------------------------------------------------------
*/

const governorates = computed(() => {
  return [
    ...new Set(
      facilities.value
        .map((facility) => facility.governorate)
        .filter((value) => value && value !== "-")
    ),
  ];
});

/*
|--------------------------------------------------------------------------
| Districts
|--------------------------------------------------------------------------
*/

const districts = computed(() => {
  let data = facilities.value;

  if (governorateFilter.value) {
    data = data.filter(
      (facility) => facility.governorate === governorateFilter.value
    );
  }

  return [
    ...new Set(
      data
        .map((facility) => facility.district)
        .filter((value) => value && value !== "-")
    ),
  ];
});

/*
|--------------------------------------------------------------------------
| Facility types
|--------------------------------------------------------------------------
*/

const facilityTypes = computed(() => {
  return [
    ...new Set(
      facilities.value
        .map((facility) => facility.type)
        .filter((value) => value && value !== "-")
    ),
  ];
});

/*
|--------------------------------------------------------------------------
| Filtered facilities
|--------------------------------------------------------------------------
*/

const filteredFacilities = computed(() => {
  const keyword = search.value.trim().toLowerCase();

  return facilities.value.filter((facility) => {
    const matchesSearch =
      !keyword || String(facility.name).toLowerCase().includes(keyword);

    const matchesGovernorate =
      !governorateFilter.value ||
      facility.governorate === governorateFilter.value;

    const matchesDistrict =
      !districtFilter.value || facility.district === districtFilter.value;

    const matchesType = !typeFilter.value || facility.type === typeFilter.value;

    const matchesPriority =
      !priorityFilter.value || facility.priority === priorityFilter.value;

    return (
      matchesSearch &&
      matchesGovernorate &&
      matchesDistrict &&
      matchesType &&
      matchesPriority
    );
  });
});

/*
|--------------------------------------------------------------------------
| Open facility
|--------------------------------------------------------------------------
*/

const openFacility = (facility) => {
  router.push(`/facilities/${facility.surveyId}`);
};

/*
|--------------------------------------------------------------------------
| Lifecycle
|--------------------------------------------------------------------------
*/

onMounted(() => {
  loadFacilities();
});
</script>

<style scoped>
.facilities-page {
  padding: 32px;
  background: #f3f5f8;
  min-height: 100vh;
  font-family: Cairo;
}

.header {
  margin-bottom: 22px;
}

.facilities-page[dir="rtl"] .header {
  text-align: right;
}

.facilities-page[dir="ltr"] .header {
  text-align: left;
}

.header h1 {
  margin: 0 0 10px;
  font-size: 32px;
  font-weight: 900;
  color: #0f2340;
}

.header p {
  margin: 0;
  color: #5f6f85;
  font-size: 14px;
  line-height: 1.8;
}

/* ================= Loading ================= */

.loading-wrapper {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.93);
  backdrop-filter: blur(5px);
}

.loading-card {
  width: 320px;
  max-width: 90%;
  padding: 34px 24px;
  text-align: center;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #dde4ec;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.loading-card h3 {
  margin-top: 18px;
  font-size: 18px;
  font-weight: 800;
}

.loading-card p {
  margin-top: 8px;
  color: #64748b;
}

/* ================= Error ================= */

.error-box {
  padding: 24px;
  text-align: center;
  color: #b91c1c;
  background: #fff1f1;
  border: 1px solid #fecaca;
  border-radius: 12px;
  font-weight: 700;
}

/* ================= Toolbar ================= */

.toolbar {
  display: grid;
  grid-template-columns:
    170px
    170px
    150px
    170px
    minmax(260px, 1fr)
    170px;
  gap: 12px;
  margin-bottom: 24px;
}

.toolbar input,
.toolbar select {
  width: 100%;
  height: 52px;
  padding: 0 14px;
  border: 1px solid #cfd7e3;
  border-radius: 10px;
  background: #fff;
  outline: none;
  font-size: 14px;
}

.toolbar input:focus,
.toolbar select:focus {
  border-color: #163d69;
  box-shadow: 0 0 0 3px rgba(22, 61, 105, 0.12);
}

.facilities-page[dir="rtl"] .toolbar input,
.facilities-page[dir="rtl"] .toolbar select {
  text-align: right;
}

.count {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 52px;
  border-radius: 10px;
  background: #eef3f8;
  font-weight: 800;
  color: #52657d;
}

/* ================= Table ================= */

.table-container {
  width: 100%;
  overflow: hidden;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

thead {
  background: #163d69;
}

thead th {
  color: #fff;
  padding: 15px 10px;
  font-size: 13px;
  font-weight: 800;
}

tbody td {
  padding: 15px 10px;
  border-bottom: 1px solid #e8edf4;
  font-size: 14px;
  color: #334155;

  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.facilities-page[dir="rtl"] th,
.facilities-page[dir="rtl"] td {
  text-align: center;
}

.facilities-page[dir="ltr"] th,
.facilities-page[dir="ltr"] td {
  text-align: center;
}

tbody tr {
  transition: 0.2s;
  cursor: pointer;
}

tbody tr:hover {
  background: #f8fafc;
}

.facility-name {
  font-weight: 800;
  color: #0f2340;
}

.fis {
  text-align: center !important;
  font-weight: 900;
}

/* ================= Status ================= */

.status-pill,
.priority-pill {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 70px;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.status-full {
  color: #0d8a43;
  background: #e7f8ee;
}

.status-partial {
  color: #cc6f00;
  background: #fff3dc;
}

.status-danger {
  color: #b91c1c;
  background: #fee2e2;
}

.status-medium {
  color: #2563eb;
  background: #e7efff;
}

.priority-pill {
  color: white;
}

.priority-pill.P1 {
  background: #dc2626;
}

.priority-pill.P2 {
  background: #ea580c;
}

.priority-pill.P3 {
  background: #2563eb;
}

.priority-pill.P4 {
  background: #16a34a;
}

.empty-row {
  padding: 30px !important;
  text-align: center !important;
  color: #64748b;
  font-weight: 800;
}

/* ================= Responsive ================= */

@media (max-width: 1200px) {
  .toolbar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  table {
    table-layout: auto;
  }
}

@media (max-width: 768px) {
  .facilities-page {
    padding: 16px;
  }

  .header {
    margin-bottom: 18px;
  }

  .header h1 {
    font-size: 24px;
    line-height: 1.5;
  }

  .header p {
    font-size: 13px;
    line-height: 1.7;
  }

  .toolbar {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 18px;
  }

  .toolbar input,
  .toolbar select,
  .count {
    width: 100%;
    height: 48px;
    font-size: 13px;
  }

  .table-container {
    overflow: visible;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
  }

  table,
  thead,
  tbody,
  tr,
  th,
  td {
    display: block;
    width: 100%;
  }

  table {
    table-layout: auto;
  }

  thead {
    display: none;
  }

  tbody {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  tbody tr {
    padding: 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  }

  tbody tr:hover {
    background: #fff;
  }

  tbody td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px dashed #e2e8f0;
    font-size: 13px;
    line-height: 1.6;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    text-align: end;
  }

  tbody td:last-child {
    border-bottom: 0;
  }

  tbody td::before {
    content: attr(data-label);
    flex: 0 0 42%;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.5;
    text-align: start;
  }

  .facility-name {
    color: #0f2340;
    font-weight: 900;
  }

  .fis {
    text-align: end !important;
  }

  .status-pill,
  .priority-pill {
    min-width: 70px;
    padding: 6px 11px;
    font-size: 11px;
  }

  .empty-row {
    display: block;
    padding: 24px !important;
    text-align: center !important;
  }

  .empty-row::before {
    display: none;
    content: none;
  }

  .loading-card {
    width: calc(100% - 32px);
    max-width: 320px;
    padding: 28px 20px;
  }

  .error-box {
    padding: 18px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .facilities-page {
    padding: 12px;
  }

  .header h1 {
    font-size: 21px;
  }

  .header p {
    font-size: 12px;
  }

  .toolbar input,
  .toolbar select,
  .count {
    height: 46px;
    font-size: 12px;
  }

  tbody {
    gap: 12px;
  }

  tbody tr {
    padding: 12px;
    border-radius: 14px;
  }

  tbody td {
    gap: 10px;
    padding: 9px 0;
    font-size: 12px;
  }

  tbody td::before {
    flex-basis: 44%;
    font-size: 10px;
  }

  .status-pill,
  .priority-pill {
    min-width: 62px;
    padding: 5px 9px;
    font-size: 10px;
  }
}
</style>
